import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  Injector,
  ReflectiveInjector,
  ComponentFactoryResolver,
  ViewContainerRef,
  OnInit
} from '@angular/core';

import {
  trigger,
  state,
  style,
  animate,
  transition,
  AnimationEvent
} from '@angular/animations';

import * as _ from 'lodash';

import { Drawer, DrawerRef, DrawerParams } from '../../drawer.service';

@Component({
  selector: 'uex-drawer',
  templateUrl: './drawer.component.html',
  animations: [
    trigger('drawer', [
      state(
        'initial',
        style({
          transform: 'translateX(100%)',
          opacity: 0,
          visibility: 'hidden'
        })
      ),
      state(
        'hidden',
        style({
          transform: 'translateX(100%)',
          opacity: 0,
          visibility: 'hidden'
        })
      ),
      state(
        'shown',
        style({ transform: 'translateX(0)', opacity: 1, visibility: 'visible' })
      ),
      transition('hidden => shown', animate(DrawerComponent.ANIMATION_TIME)),
      transition('shown => hidden', animate(DrawerComponent.ANIMATION_TIME))
    ]),
    trigger('backdrop', [
      state('initial', style({ opacity: 0, visibility: 'hidden' })),
      state('hidden', style({ opacity: 0, visibility: 'hidden' })),
      state('shown', style({ opacity: 1, visibility: 'visible' })),
      transition('hidden => shown', animate(DrawerComponent.ANIMATION_TIME)),
      transition('shown => hidden', animate(DrawerComponent.ANIMATION_TIME))
    ])
  ]
})
export class DrawerComponent implements OnInit {
  @ViewChild('content', { read: ViewContainerRef, static: true }) content: ViewContainerRef;
  @ViewChild('drawerContent', { static: true }) drawerContent: ElementRef;
  @ViewChild('drawerWrapper', { static: true }) drawerWrapper: ElementRef;

  @Input('drawer') drawer: Drawer;

  public static ANIMATION_TIME: number = 100;

  constructor(private _compiler: ComponentFactoryResolver) {}

  public ready = false;
  public get shown(): boolean {
    return this._shown;
  }
  public get visible(): boolean {
    return this._visible;
  }
  public set visible(visible: boolean) {
    if (visible) {
      this._visible = true;

      setTimeout(() => {
        this._shown = true;
      }, 0);
    } else {
      this._shown = false;

      setTimeout(() => {
        this._visible = false;
      }, DrawerComponent.ANIMATION_TIME + 50);
    }
  }

  private _visible: boolean = false;
  private _shown: boolean = false;

  public ngOnInit() {
    const drawerRef: DrawerRef = new DrawerRef();
    const drawerParams = this.drawer.params;
    drawerRef.drawer = this.drawer;
    const injector: Injector = ReflectiveInjector.resolveAndCreate([
      { provide: DrawerRef, useValue: drawerRef },
      { provide: DrawerRef, useValue: drawerRef },
      { provide: DrawerParams, useValue: drawerParams },
      { provide: DrawerParams, useValue: drawerParams }
    ]);
    const compiled_component = this._compiler.resolveComponentFactory(
      this.drawer.component
    );
    this.drawer.componentRef = this.content.createComponent(
      compiled_component,
      null,
      injector
    );
    this.drawer.componentInstance = this;
    this.ready = true;
  }

  public animationStart(event: AnimationEvent) {
    if (event.fromState == 'shown' && event.toState == 'hidden') {
      this.drawer.onHide.emit();
    }
  }

  public animationEnd(event: AnimationEvent) {
    if (event.fromState == 'hidden' && event.toState == 'shown') {
      this.drawer.onShown.emit();
    }
    if (event.fromState == 'shown' && event.toState == 'hidden') {
      this.drawer.onHidden.emit();
    }
  }

  public getState(): string {
    return this.ready ? (this.shown ? 'shown' : 'hidden') : 'initial';
  }

  public backdropClick(event: MouseEvent) {
    const target: any = event.target;
    if (target && !target.classList.contains('uex-drawer-modal')) {
      return;
    }
    switch (this.drawer.settings.backdrop.onClick) {
      case 'close':
        this.drawer.close();
        break;
      case 'dismiss':
        this.drawer.dismiss();
        break;
      case 'none':
        break;
      default:
        this.drawer.close();
    }
  }

  public headerCloseClick() {
    if (!this.drawer.settings.header.closable) {
      return;
    }
    switch (this.drawer.settings.header.closeClick) {
      case 'close':
        this.drawer.close();
        break;
      case 'dismiss':
        this.drawer.dismiss();
        break;
      default:
        this.drawer.close();
    }
  }

  public getSizeClass(): string {
    return this.drawer.sizeClass();
  }

  public getModalZIndex(): any {
    return this.drawer.zindex;
  }

  public getModalContentZIndex(): any {
    return this.drawer.content_zindex;
  }

  public getBackdropZIndex(): any {
    return this.drawer.backdrop_zindex;
  }
}
